// src/services/authService.ts
import api from './api';
import axios, { AxiosError } from 'axios';

// Keep track of recent API calls to avoid rate limiting
let lastApiCallTime = 0;
const MIN_API_CALL_INTERVAL = 1000; // 1 second minimum between calls

// Helper function to delay execution to avoid rate limiting
const delayIfNeeded = async (): Promise<void> => {
  const now = Date.now();
  const timeSinceLastCall = now - lastApiCallTime;
  
  if (timeSinceLastCall < MIN_API_CALL_INTERVAL) {
    const delayTime = MIN_API_CALL_INTERVAL - timeSinceLastCall;
    await new Promise(resolve => setTimeout(resolve, delayTime));
  }
  
  lastApiCallTime = Date.now();
};

// Helper function to implement retry logic with exponential backoff
const retryWithBackoff = async <T>(fn: () => Promise<T>, maxRetries = 3, initialDelay = 2000): Promise<T> => {
  let retries = 0;
  
  while (true) {
    try {
      return await fn();
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 429) {
        // Rate limit error
        if (retries >= maxRetries) {
          throw new Error('Maximum retry attempts reached. Please try again later.');
        }
        
        const delay = initialDelay * Math.pow(2, retries);
        console.log(`Rate limit hit. Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        retries++;
      } else {
        // Different error - just throw it
        throw error;
      }
    }
  }
};

export const verifyToken = async (token: string) => {
  try {
    await delayIfNeeded();
    
    return await retryWithBackoff(async () => {
      const response = await api.post('/auth/verify', { token });
      return response.data;
    });
  } catch (error) {
    console.error('Error verifying token:', error);
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 429) {
      throw new Error('Authentication service is busy. Please try again in a few moments.');
    }
    throw error;
  }
};

export const refreshToken = async (privyUserId: string) => {
  try {
    await delayIfNeeded();
    
    return await retryWithBackoff(async () => {
      const response = await api.post('/auth/refresh', { privyUserId });
      return response.data;
    });
  } catch (error) {
    console.error('Error refreshing token:', error);
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 429) {
      throw new Error('Authentication service is busy. Please try again in a few moments.');
    }
    throw error;
  }
};

export const createOrUpdateUser = async (privyUserId: string) => {
  try {
    await delayIfNeeded();
    
    return await retryWithBackoff(async () => {
      const response = await api.post('/auth/user', { privyUserId });
      return response.data;
    });
  } catch (error) {
    console.error('Error creating/updating user:', error);
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 429) {
      throw new Error('User service is busy. Please try again in a few moments.');
    }
    throw error;
  }
};

export const createEmbeddedWallet = async (privyUserId: string) => {
  try {
    await delayIfNeeded();
    
    return await retryWithBackoff(async () => {
      const response = await api.post('/auth/wallet', { privyUserId });
      return response.data;
    });
  } catch (error) {
    console.error('Error creating embedded wallet:', error);
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 429) {
      throw new Error('Wallet service is busy. Please try again in a few moments.');
    }
    throw error;
  }
};